import { Injectable } from "@angular/core";
import { GeneralRequestService } from "./general-request.service";
import { UserInfoService } from "./user-info.service";
import { NgThrottle } from "../decorators/throttle.decorator";
import { SystemNotification } from "../models/system-notifications/system-notification";
import { SystemNotificationConfirmation } from "../models/system-notifications/system-notification-confirmation";
import { ArraysHelper } from "./aids/arrays.helper";
import { SessionStorageHelper } from "./aids/session-storage.helper";
import { environment } from "../../environments/environment";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class SystemNotificationsService {
  beforeLoginFetchDone = false;
  afterLoginFetchDone = false;
  private allConfirmationsArray: SystemNotificationConfirmation[] = [];
  private allSystemNotificationsArrayAfterLogin: SystemNotification[] = [];
  private allSystemNotificationsArrayBeforeLogin: SystemNotification[] = [];
  private readonly readNotificationsArrayKey = "readSystemNotifications";
  private readonly fetchBeforeLoginNotificationsUrl: string = environment.backend.baseUrl
    + environment.backend.contextPath
    + "/rest/patient-system-messages/";

  constructor(private generalRequestService: GeneralRequestService,
              private userService: UserInfoService,
              private sessionStorageHelper: SessionStorageHelper,
              private http: HttpClient) {
  }

  private get notificationsToShowAfterLogin(): SystemNotification[] {
    const readInSession = this.readNotificationsIds;
    return this.allSystemNotificationsArrayAfterLogin.filter(message => this.filterNotification(message, readInSession, true));
  }

  private get notificationsToShowBeforeLogin(): SystemNotification[] {
    const readInSession = this.readNotificationsIds;
    return this.allSystemNotificationsArrayBeforeLogin.filter(message => this.filterNotification(message, readInSession, false));
  }

  private get readNotificationsIds() {
    return this.sessionStorageHelper.retrieveObjectFromSessionStorage<number[]>(this.readNotificationsArrayKey) ?? [];
  }

  addConfirmationsToArray(newItems: SystemNotificationConfirmation[]) {
    this.allConfirmationsArray = ArraysHelper.mergeArraysByObjId(this.allConfirmationsArray, newItems);
  }

  filterNotification(message: SystemNotification, readInSession: number[], isLoggedIn: boolean) {
    if (!message.active) return false;
    if (new Date(message.startDate) > new Date()) return false;
    if (new Date(message.endDate) < new Date()) return false;
    if (readInSession.some(id => message.id === id)) return false;
    if (!isLoggedIn) return true;
    return !this.allConfirmationsArray.some(confirmation => confirmation.systemMessageId == message.id && confirmation.hasSeenMessage);
  }

  addNotificationsToArray(newItems: SystemNotification[]) {
    this.allSystemNotificationsArrayAfterLogin = ArraysHelper.mergeArraysByObjId(this.allSystemNotificationsArrayAfterLogin, newItems);
  }

  markSystemNotificationAsReadInSession(messageId: number) {
    const currentRead = this.readNotificationsIds;
    currentRead.push(messageId);
    this.sessionStorageHelper.saveObjectToSessionStorage(this.readNotificationsArrayKey, currentRead);
  }

  async fetchSystemNotificationsAfterLogin() {
    let notifications = await this.generalRequestService.fetchSystemMessages();
    if (notifications!.items.length > 0) {
      this.addNotificationsToArray(notifications!.items);
    }
  }

  async getFetchedAndFilteredNotificationsAfterLogin() {
    if (!this.afterLoginFetchDone) {
      await this.fetchSystemNotificationsAfterLogin();
      await this.fetchSystemNotificationsConfirmations();
      this.afterLoginFetchDone = true;
    }
    return this.notificationsToShowAfterLogin;
  }

  async fetchSystemNotificationsConfirmations() {
    const personId = this.userService.personId;
    if (!personId) {
      console.error("Could not get person ID");
      return;
    }
    let confirmations = await this.generalRequestService.fetchSystemNotificationConfirmations(personId);
    if (confirmations!.items.length > 0) {
      this.addConfirmationsToArray(confirmations!.items);
    }
  }

  @NgThrottle(500)
  async markNotificationAsRead(message: SystemNotification) {
    const personId = this.userService.personId;
    if (!personId) {
      console.error("Could not get person ID");
      return;
    }
    let newConfirmation = await this.generalRequestService.markNotificationAsRead(message, personId);
    if (newConfirmation!.items.length > 0) {
      this.addConfirmationsToArray(newConfirmation!.items);
    }
  }

  async getFetchedAndFilteredNotificationsBeforeLogin() {
    if (!this.beforeLoginFetchDone) {
      let notifications = await this.getNotificationsBeforeLogin();
      this.allSystemNotificationsArrayBeforeLogin = notifications.body!;
      this.beforeLoginFetchDone = true;
    }
    return this.notificationsToShowBeforeLogin;
  }

  async getNotificationsBeforeLogin() {
    return await firstValueFrom(this.http.get<SystemNotification[]>(this.fetchBeforeLoginNotificationsUrl, {
      observe: "response",
      withCredentials: true,
    }));
  }
}
